import { ChevronDown, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { CONTROLS } from '@brickup/shared/locales/de/controls'
import { Button, cn, ReadOnlyScope, Select } from '@/components/base'
import {
  FILTER_CHIP_ACTIVE,
  FILTER_CHIP_CLASS,
  FILTER_CONTROL_WIDTH,
  FILTER_ROW_CLASS,
  FILTER_ROW_LANE_CLASS,
  FilterChip,
  FilterChipField,
} from '@/components/Internal/FilterChip'
import { SearchInput } from '@/components/Internal/SearchInput'
import {
  ToolbarViews,
  type ToolbarViewsProps,
} from '@/components/Internal/ToolbarViews'

// ─── Items ──────────────────────────────────────────────────────────────────

// `any` on purpose: the union below must accept a pill group of any value
// type, and `unknown` makes the `onChange` parameter contravariant so a
// concrete group stops being assignable.

/** One value out of a few, all of them on screen. */
export type PillsToolbarItem<V = any> = {
  type: 'pills'
  options: { value: V; label: string }[]
  value: V
  onChange: (value: V) => void
}

/** Several values out of many — a tag set, where `count` lets the reader pick
 *  the bucket by seeing how full it is. */
export type ChipsToolbarItem = {
  type: 'chips'
  options: { value: string; label: string; count?: number }[]
  value: string[]
  onChange: (value: string[]) => void
}

export type SelectToolbarItem = {
  type: 'select'
  options: { value: string; label: string }[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

export type SearchToolbarItem = {
  type: 'search'
  value: string
  onChange: (value: string) => void
  placeholder?: string
  width?: string
}

/** A condition that is either on or off — "only unused", "only mine". */
export type ToggleToolbarItem = {
  type: 'toggle'
  label: string
  value: boolean
  onChange: (value: boolean) => void
  icon?: LucideIcon
}

/** A lower bound the reader types — a minimum width, a minimum count. */
export type NumberToolbarItem = {
  type: 'number'
  label: string
  value: number | null
  onChange: (value: number | null) => void
}

/**
 * How many rows the list is showing, and out of how many.
 *
 * The label is derived rather than written by each screen: "3 von 340 Objekte"
 * the moment a filter narrows the list and "340 Objekte" otherwise, which is a
 * rule every list follows and none should restate.
 */
export type ToolbarCount = {
  /** Rows matching the current filters. */
  total: number
  /**
   * Rows the endpoint returns with no filter set. Omitted where there is no
   * such figure, which reduces the label to the plain total.
   */
  unfilteredTotal?: number
  /**
   * The caller's noun for its rows. A function where the noun inflects — "1
   * Bild" against "2 Bilder"; a plain string where German uses one form.
   */
  unit: string | ((count: number) => string)
}

export type ToolbarItem =
  | PillsToolbarItem
  | ChipsToolbarItem
  | SelectToolbarItem
  | SearchToolbarItem
  | ToggleToolbarItem
  | NumberToolbarItem

// ─── The bar ────────────────────────────────────────────────────────────────

/**
 * The filter bar over a list: what is shown on the left, how many and the
 * controls over the view on the right.
 *
 * Every control in it wears `FILTER_CHIP_CLASS`, so a row of them keeps one
 * height and one shape whatever mix a screen declares. `toolbarItems` is data,
 * which is what lets a list, a grid and a tree filter the same way without
 * each restating the row.
 */
export function FilterToolbar({
  toolbarItems,
  hasActiveFilters,
  onResetFilters,
  count,
  countLabel,
  views,
  toolbarExtra,
}: {
  toolbarItems?: ToolbarItem[]
  hasActiveFilters?: boolean
  onResetFilters?: () => void
  count?: ToolbarCount
  /** Overrides `count` with a literal label. */
  countLabel?: string
  /** The filters this list keeps under a name. One of them is the state of
   *  the whole row, so they stand beside the count they produce. */
  views?: Omit<ToolbarViewsProps, 'onReset'>
  /** Controls over how the list is drawn — a view switch, a tile size. They
   *  sit apart from the filters because they change nothing about what is in
   *  it. */
  toolbarExtra?: ReactNode
}) {
  const shownCount = countLabel ?? describeCount(count)

  if (!toolbarItems?.length && !shownCount && !views && !toolbarExtra) {
    return null
  }

  return (
    <div className={cn(FILTER_ROW_CLASS, 'border-line-soft')}>
      {/* Narrowing a list is reading it, so the filters declare themselves
          live even where the screen around them refuses changes. What a
          screen hangs in `toolbarExtra` is its own — an add button belongs to
          the page, and stays subject to it. */}
      <ReadOnlyScope reads>
        <div className={FILTER_ROW_LANE_CLASS}>
          {/* Counted per kind, so a control a screen only sometimes declares
              cannot shift the key of every control after it — which remounts
              them and drops the focus of the chip that was just pressed. */}
          {keyedItems(toolbarItems).map(([key, item]) => (
            <ToolbarItemView key={key} item={item} />
          ))}
          {hasActiveFilters && onResetFilters ? (
            <Button
              reads
              variant="ghost"
              size="icon"
              onClick={onResetFilters}
              aria-label={CONTROLS.filter.reset}
              icon={X}
            />
          ) : null}
        </div>
      </ReadOnlyScope>

      <div className="flex shrink-0 flex-row items-center gap-3">
        {shownCount ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {shownCount}
          </span>
        ) : null}
        {views && onResetFilters ? (
          <ReadOnlyScope reads>
            <ToolbarViews {...views} onReset={onResetFilters} />
          </ReadOnlyScope>
        ) : null}
        {toolbarExtra}
      </div>
    </div>
  )
}

/** "3 von 340 Objekte" while a filter narrows the list, "340 Objekte" else. */
function describeCount(count?: ToolbarCount): string | undefined {
  if (!count) return undefined
  const { total, unfilteredTotal, unit } = count
  const noun = typeof unit === 'function' ? unit(total) : unit
  return unfilteredTotal !== undefined && unfilteredTotal !== total
    ? CONTROLS.toolbar.countOfTotal(total, unfilteredTotal, noun)
    : CONTROLS.toolbar.count(total, noun)
}

/** Each item with a key that survives a sibling of another kind appearing. */
function keyedItems(items: ToolbarItem[] = []): [string, ToolbarItem][] {
  const seen = new Map<string, number>()
  return items.map(item => {
    const next = (seen.get(item.type) ?? 0) + 1
    seen.set(item.type, next)
    return [`${item.type}-${next}`, item]
  })
}

function ToolbarItemView({ item }: { item: ToolbarItem }) {
  switch (item.type) {
    case 'pills':
      return (
        <div className="flex flex-row gap-1.5">
          {item.options.map(option => (
            <FilterChip
              key={String(option.value)}
              label={option.label}
              active={item.value === option.value}
              onClick={() => item.onChange(option.value)}
            />
          ))}
        </div>
      )

    case 'chips':
      return (
        <div className="flex flex-row flex-wrap gap-1.5">
          {item.options.map(option => (
            <FilterChip
              key={option.value}
              label={option.label}
              count={option.count}
              active={item.value.includes(option.value)}
              onClick={() =>
                item.onChange(
                  item.value.includes(option.value)
                    ? item.value.filter(each => each !== option.value)
                    : [...item.value, option.value],
                )
              }
            />
          ))}
        </div>
      )

    case 'toggle':
      return (
        <FilterChip
          label={item.label}
          icon={item.icon}
          active={item.value}
          onClick={() => item.onChange(!item.value)}
        />
      )

    case 'select':
      return <ToolbarSelect item={item} />

    case 'number':
      return (
        <FilterChipField
          type="number"
          leading={item.label}
          value={item.value == null ? '' : String(item.value)}
          onChange={raw => {
            const next = Number(raw)
            item.onChange(raw && Number.isFinite(next) ? next : null)
          }}
          active={item.value != null}
        />
      )

    case 'search':
      return (
        <SearchInput
          value={item.value}
          onChange={item.onChange}
          placeholder={item.placeholder}
          width={item.width}
        />
      )
  }
}

function ToolbarSelect({ item }: { item: SelectToolbarItem }) {
  const selected = item.options.find(option => option.value === item.value)
  // The first option is the "all" one, so choosing it is not a filter and the
  // chip stays quiet.
  const isFiltering = !!selected && item.value !== item.options[0]?.value

  const shown = isFiltering
    ? selected.label
    : (item.placeholder ?? selected?.label)

  const trigger = () => (
    <span
      className={cn(
        FILTER_CHIP_CLASS,
        isFiltering && FILTER_CHIP_ACTIVE,
        // As wide as what it says: a row of chips sized to their widest
        // option reads as if something were missing from them. The row
        // reflows when a filter changes, which is the cost.
        FILTER_CONTROL_WIDTH,
      )}
      // The chip is rendered inside the Radix trigger, which is the button.
      role="presentation"
    >
      <span className="truncate text-left">{shown}</span>
      <ChevronDown size={13} className={isFiltering ? '' : 'opacity-50'} />
    </span>
  )

  return (
    <Select
      value={item.value}
      onChange={item.onChange}
      options={item.options}
      placeholder={item.placeholder}
      trigger={trigger}
    />
  )
}
